import { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/lib/i18n';
import { beatsItems, beatTypes } from "@/data/beats.generated";
import { beatUrl } from "@/lib/beatUrl";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Play, Pause, Rewind, FastForward } from 'lucide-react';


export default function BeatsGrid({ limit }) {
  const { t } = useLanguage();
  const [selectedType, setSelectedType] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);

  const filtered = useMemo(() => {
    let items = beatsItems;
    if (selectedType !== 'All') items = items.filter(i => i.type === selectedType);
    if (limit) items = items.slice(0, limit);
    return items;
  }, [selectedType, limit]);

  return (
    <div data-testid="beats-grid">
      {/* Filters */}
      {!limit && (
        <div className="mb-12 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="type-filters">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/20 self-center mr-2">Type</span>
            {beatTypes.map(tp => (
              <button
                key={tp}
                data-testid={`filter-type-${tp.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setSelectedType(tp)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${selectedType === tp
                  ? 'bg-[#00E5FF] text-black shadow-[0_0_20px_rgba(0,229,255,0.25)]'
                  : 'bg-white/[0.03] text-white/40 hover:text-white/70 hover:bg-white/[0.06] border border-white/[0.06]'
                  }`}
              >

                {tp === 'All' ? t('portfolio_filter_all') : (tp === 'Diğerleri' ? t('form_genre_other') : tp)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filtered.map((item, i) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
            >
              <button
                data-testid={`beats-card-${item.id}`}
                onClick={() => setSelectedItem(item)}
                className="w-full text-left bg-[#0A0A0A] border border-[#1F1F1F] rounded-2xl overflow-hidden hover:border-[#7F00FF]/40 transition-all duration-300 group relative"
              >
                {/* Card gradient visual */}
                <div className="relative p-5 pb-0">
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
                </div>

                <div className="p-5">
                  <h3 className="font-syne font-bold text-lg mb-1.5 group-hover:text-[#7F00FF] transition-colors duration-200">
                    {item.title}
                  </h3>
                  <span className="text-sm text-white/40 mb-1 block">{item.genre}</span>
                </div>
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-white/25 py-20 font-mono text-sm" data-testid="beats-no-results">
          {t('portfolio_no_results')}
        </p>
      )}

      <Dialog
        open={!!selectedItem}
        onOpenChange={(open) => !open && setSelectedItem(null)}
      >
        <DialogContent
          className="bg-[#0A0A0A] border-white/[0.08] max-w-lg"
          data-testid="beats-modal"
        >
          {selectedItem && <BeatPlayer item={selectedItem} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BeatPlayer({ item }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const src = beatUrl(item);


  /* PROGRESS */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const update = () => {
      const percent =
        (audio.currentTime / audio.duration) * 100 || 0;
      setProgress(percent);
    };

    audio.addEventListener("timeupdate", update);

    return () => {
      audio.removeEventListener("timeupdate", update);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);


  /* PLAY / PAUSE */
  const togglePlay = () => {
    if (!audioRef.current) return;

    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }

    setPlaying(!playing);
  };

  const skip = (seconds) => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    audio.currentTime = Math.min(
      Math.max(0, audio.currentTime + seconds),
      audio.duration || 0
    );
  };

  const formatTime = (time) => {
    if (!time) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  return (
    <>
      {/* TITLE */}
      <DialogHeader>
        <DialogTitle className="font-syne text-2xl">
          {item.title}
        </DialogTitle>

        <DialogDescription className="text-sm text-white/40 mb-6">
          {item.genre}
        </DialogDescription>
      </DialogHeader>

      {/* COVER */}
      {item.cover && (
        <div className="mb-6">
          <img
            src={item.cover}
            alt={item.title}
            className="w-full h-56 object-cover rounded-xl"
          />
        </div>
      )}

      <div className="flex items-center gap-4">

        {/* BACK 10s */}
        <button
          onClick={() => skip(-10)}
          className="text-white/60 hover:text-white transition"
        >
          <div className="relative">
            <Rewind className="w-6 h-6" />
            <span className="absolute -bottom-2 -right-2 text-[8px]">
              10
            </span>
          </div>
        </button>

        {/* PLAY */}
        <button
          onClick={togglePlay}
          className="
      w-14 h-14
      flex items-center justify-center
      rounded-full
      bg-white text-black
      hover:scale-105
      transition
    "
        >
          {playing ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-1" />
          )}
        </button>

        {/* FORWARD 10s */}
        <button
          onClick={() => skip(10)}
          className="text-white/60 hover:text-white transition"
        >
          <div className="relative">
            <FastForward className="w-6 h-6" />
            <span className="absolute -bottom-2 -left-2 text-[8px]">
              10
            </span>
          </div>
        </button>

        {/* PROGRESS */}
        <div
          className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const width = rect.width;
            const percent = clickX / width;

            if (audioRef.current) {
              audioRef.current.currentTime =
                percent * audioRef.current.duration;
            }
          }}
        >
          <div
            className="h-full bg-gradient-to-r from-[#7F00FF] to-[#00E5FF]"
            style={{ width: `${progress}%` }}
          />
        </div>

      </div>

      <div className="text-xs text-white/40 mt-2">
        {formatTime(audioRef.current?.currentTime)} / {formatTime(audioRef.current?.duration)}
      </div>


      {/* AUDIO */}
      <audio ref={audioRef} src={src} preload="metadata" />
    </>
  );
}

